import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useFetch } from '@/hooks/use-fetch';

/**
 * AIT-370 — the calling user's private alert phone (user-scoped: /auth/phone
 * needs no workspace header, mirroring the other /auth/* self routes).
 * Backed by react-query so every consumer (launchpad row, Edit profile)
 * shares one cache: a refresh from either surface updates both.
 */
export interface AlertPhoneStatus {
  phone: string | null;
  verified: boolean;
  consents: { operational: boolean; product: boolean; marketing: boolean };
  channelPreference: string;
}

const QUERY_KEY = ['alert-phone'];

export function useAlertPhone() {
  const fetchWithAuth = useFetch();
  const queryClient = useQueryClient();

  const { data, isPending } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: async (): Promise<AlertPhoneStatus | null> => {
      // suppressGlobalError: this is a background enrichment read — a failed
      // status lookup must never take the whole app to the error page. The
      // row shows its own empty/loading state instead.
      try {
        const res = await fetchWithAuth('/auth/phone', undefined, { suppressGlobalError: true });
        if (!res.ok) return null;
        return (await res.json()) as AlertPhoneStatus;
      } catch {
        return null;
      }
    },
  });

  const refresh = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }, [queryClient]);

  return { status: data ?? null, loading: isPending, refresh };
}
